import { SQUAD_COLORS } from '../templateConstants';
import { SQUAD_PAGE_HEADER, SQUAD_BODY_LAYOUT, SQUAD_TYPOGRAPHY, SQUAD_SECTION_GEOMETRY_PREVIEW } from '../pageHeaderTokens';
import { SQUAD_ADVISOR_PHOTO } from '../squadAdvisors';
import { SQUAD_IMAGE_FORMATS } from '../squadImageFormats';

const { sidebarWidth, sidebarMaxWidth, columnGap } = SQUAD_BODY_LAYOUT;

const sidebarColumn = {
  width: sidebarWidth,
  maxWidth: sidebarMaxWidth,
  flexShrink: 0,
};

const sharedStyles = {
  pageHeader: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'flex-start',
    gap: `${columnGap}px`,
    pt: 0.5,
    pb: `${SQUAD_PAGE_HEADER.headerPaddingBottom}px`,
    mb: `${SQUAD_PAGE_HEADER.headerMarginBottom}px`,
    fontFamily: SQUAD_PAGE_HEADER.headerFontFamily,
    '& *': {
      fontFamily: 'inherit',
    },
  },
  headerSidebarCell: {
    ...sidebarColumn,
    alignSelf: 'flex-start',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
  },
  logoBlock: {
    display: 'inline-block',
    lineHeight: 1.05,
    verticalAlign: 'top' as const,
    mb: `${SQUAD_PAGE_HEADER.logoMarginBottom}px`,
  },
  headerMainCell: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: `${SQUAD_PAGE_HEADER.titleRowMetaGap}px`,
    alignSelf: 'flex-start',
  },
  headerMetaRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'space-between',
    alignItems: 'baseline',
    width: '100%',
  },
  mainTitle: {
    fontSize: SQUAD_PAGE_HEADER.titleFontSize,
    fontWeight: 400,
    textAlign: 'left' as const,
    lineHeight: SQUAD_PAGE_HEADER.titleLineHeight,
    // px instead of em — react-pdf applies letter spacing absolutely; with `em` the
    // preview title ran noticeably wider than in the PDF
    letterSpacing: `${SQUAD_PAGE_HEADER.titleLetterSpacing}px`,
  },
  titleSquad: {
    color: SQUAD_COLORS.gold,
    // Both title parts share one weight — see PdfPreviewDialog/styles.ts
    fontWeight: 600,
  },
  titleRest: {
    color: SQUAD_COLORS.navy,
    fontWeight: 600,
  },
  headerMetaText: {
    // Browsers clamp sub-~10px fonts; scale from a readable base so preview can go smaller.
    fontSize: SQUAD_PAGE_HEADER.metaFontSize,
    color: SQUAD_PAGE_HEADER.metaColor,
    lineHeight: 1.2,
    transform: `scale(${SQUAD_PAGE_HEADER.metaFontScale})`,
    display: 'inline-block',
  },
  headerMetaTextLeft: {
    transformOrigin: 'left center',
  },
  headerMetaTextRight: {
    transformOrigin: 'right center',
  },
  goldBar: {
    backgroundColor: 'transparent',
    color: SQUAD_COLORS.navy,
    fontSize: SQUAD_TYPOGRAPHY.goldBar,
    fontWeight: 700,
    textAlign: 'center' as const,
    py: `${SQUAD_SECTION_GEOMETRY_PREVIEW.goldBarPaddingVertical}px`,
    mt: `${SQUAD_SECTION_GEOMETRY_PREVIEW.goldBarMarginTop}px`,
    mb: `${SQUAD_SECTION_GEOMETRY_PREVIEW.goldBarMarginBottom}px`,
    borderTop: `1.5px solid ${SQUAD_COLORS.navy}`,
    borderBottom: `1.5px solid ${SQUAD_COLORS.navy}`,
  },
  navyBar: {
    backgroundColor: SQUAD_COLORS.navy,
    color: SQUAD_COLORS.white,
    fontSize: SQUAD_TYPOGRAPHY.navyBar,
    fontWeight: 700,
    textAlign: 'center' as const,
    py: `${SQUAD_SECTION_GEOMETRY_PREVIEW.navyBarPaddingVertical}px`,
    px: 1.3,
    mt: `${SQUAD_SECTION_GEOMETRY_PREVIEW.navyBarMarginTop}px`,
    mb: 0.9,
    borderRadius: '4px',
  },
  kvRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'flex-start',
    fontSize: SQUAD_TYPOGRAPHY.kv,
    lineHeight: 1.15,
    pl: 0,
    pr: `${SQUAD_PAGE_HEADER.pdfSidebarPaddingHorizontal}px`,
    py: `${SQUAD_SECTION_GEOMETRY_PREVIEW.kvRowPaddingVertical}px`,
    borderBottom: `0.5px solid ${SQUAD_COLORS.navy}`,
  },
  kvLabel: {
    /** 60/40 as in the original factsheet — mirrors the PDF. */
    flex: '0 0 60%',
    maxWidth: '60%',
    minWidth: 0,
    overflow: 'hidden',
    color: SQUAD_COLORS.navy,
    pl: 0,
    pr: 0.75,
    textAlign: 'left' as const,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
  },
  kvValue: {
    flex: '0 0 40%',
    maxWidth: '40%',
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'left' as const,
    color: SQUAD_COLORS.navy,
    fontWeight: 400,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
  },
  imagePlaceholder: {
    border: `1.5px dashed ${SQUAD_COLORS.goldDark}`,
    backgroundColor: '#FAF6EE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: SQUAD_COLORS.goldDark,
    fontSize: SQUAD_TYPOGRAPHY.placeholder,
    fontWeight: 600,
    textAlign: 'center' as const,
    px: 2,
    minHeight: 80,
    mb: 1,
  },
  barRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    mb: 0.6,
    fontSize: SQUAD_TYPOGRAPHY.bar,
  },
  barLabel: {
    width: '42%',
    pr: 0.75,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  barTrack: {
    flex: 1,
    height: 14,
    backgroundColor: '#EEE',
    position: 'relative' as const,
  },
  barFill: {
    height: '100%',
    backgroundColor: SQUAD_COLORS.gold,
  },
  barPct: {
    width: 44,
    textAlign: 'right' as const,
    fontWeight: 600,
    pl: 0.75,
  },
  sidebarSectionTitle: {
    fontSize: SQUAD_TYPOGRAPHY.sidebarSection,
    fontWeight: 700,
    textDecoration: 'underline',
    color: SQUAD_COLORS.navy,
    mb: 1.25,
  },
  // Fondsberater — mirror PdfPreviewDialog advisor styles
  advisorBlockFeatured: {
    mb: 0.75,
  },
  advisorBlockDuo: {
    mt: 0.2,
    mb: 0.2,
  },
  advisorFeaturedRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'flex-start',
    gap: '6px',
    mb: 0.2,
  },
  advisorFeaturedPhotoCol: {
    width: '46%',
    flexShrink: 0,
  },
  advisorFeaturedPhoto: {
    width: '100%',
    aspectRatio: `${SQUAD_ADVISOR_PHOTO.widthPx} / ${SQUAD_ADVISOR_PHOTO.heightPx}`,
    height: 'auto',
    objectFit: 'contain' as const,
    objectPosition: 'center',
    display: 'block',
    bgcolor: SQUAD_COLORS.white,
  },
  advisorFeaturedLogoCol: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    // Match photo column height at sidebar scale (B/H frame).
    minHeight: 56,
    pl: '4px',
  },
  advisorFeaturedLogoLine1: {
    fontFamily: SQUAD_PAGE_HEADER.headerFontFamily,
    fontSize: `${SQUAD_TYPOGRAPHY.advisorLogoLine1}px`,
    fontWeight: 700,
    color: SQUAD_COLORS.navy,
    textAlign: 'left' as const,
    lineHeight: 1.05,
    letterSpacing: '0.02em',
  },
  advisorFeaturedLogoLine2: {
    fontFamily: SQUAD_PAGE_HEADER.headerFontFamily,
    fontSize: `${SQUAD_TYPOGRAPHY.advisorLogoLine2}px`,
    fontWeight: 700,
    color: SQUAD_COLORS.gold,
    textAlign: 'left' as const,
    lineHeight: 1.05,
    letterSpacing: '0.06em',
    mt: 0.1,
  },
  advisorDuoRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: '5px',
    mb: 0.25,
  },
  advisorDuoCol: {
    flex: 1,
    minWidth: 0,
  },
  advisorDuoPhoto: {
    width: '100%',
    height: 54,
    objectFit: 'cover' as const,
    objectPosition: 'top center',
    display: 'block',
    bgcolor: SQUAD_COLORS.white,
    mb: 0.15,
  },
  advisorName: {
    fontWeight: 700,
    fontSize: `${SQUAD_TYPOGRAPHY.advisorName}px`,
    lineHeight: 1.1,
    mb: 0.15,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  },
  advisorBio: {
    fontSize: `${SQUAD_TYPOGRAPHY.advisorBio}px`,
    lineHeight: 1.12,
    // Justified, as the PDF has always been — the two had drifted apart
    textAlign: 'justify' as const,
    // The bio is sanitized HTML now; browser paragraph margins would open gaps the
    // PDF does not have (RichHtmlPdf spaces paragraphs itself).
    '& p': { margin: 0 },
    '& p + p': { marginTop: '6px' },
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  },
  advisorFooterLogo: {
    width: '100%',
    // 1280 × 540 frame (ca. 21:9) drawn edge to edge — the file carries its own margins
    aspectRatio: `${SQUAD_IMAGE_FORMATS.partnerLogo.widthPx} / ${SQUAD_IMAGE_FORMATS.partnerLogo.heightPx}`,
    height: 'auto',
    objectFit: 'contain' as const,
    mt: 0.5,
    display: 'block',
  },
  /** Optional logo pinned to the foot of the page-2 sidebar. */
  sidebarBottomLogo: {
    width: '100%',
    aspectRatio: `${SQUAD_IMAGE_FORMATS.partnerLogo.widthPx} / ${SQUAD_IMAGE_FORMATS.partnerLogo.heightPx}`,
    height: 'auto',
    objectFit: 'contain' as const,
    objectPosition: 'bottom',
    display: 'block',
    mt: 'auto',
  },
};

export default sharedStyles;
